import React, { useState } from 'react'
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/20/solid'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Checkbox,
  Collapse,
  FormControl,
  HStack,
  IconButton,
  Input,
  Link,
  Stack,
  Text
} from '@chakra-ui/react'

import axios from 'lib/axios'

import InfoHint from '../components/InfoHint'
import { getErrorMessage } from '../types'

type Props = {
  statementEndDate: string
}

const TestSend = ({ statementEndDate }: Props) => {
  const [skipEmail, setSkipEmail] = useState(false)
  const [debugRentalId, setDebugRentalId] = useState('')
  const [showDebugRentalId, setShowDebugRentalId] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const toggleDebugRentalId = () => {
    setShowDebugRentalId((previous) => {
      if (previous) {
        setDebugRentalId('')
      }
      return !previous
    })
  }

  const onSend = async () => {
    try {
      setIsLoading(true)
      setError('')
      setSuccess('')

      await axios.post('/admin/system/topic_publish', {
        topic: 'calculator-send-monthly-statements',
        message: {
          skipEmail,
          debugRentalId: debugRentalId || null,
          statementEndDate
        }
      })

      setSuccess('Test send triggered. Verify expected statement counts in GCF logs.')
    } catch (err: any) {
      setError(getErrorMessage(err))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Stack spacing={3}>
      <HStack spacing={1} alignSelf='flex-start' alignItems='center'>
        <Link
          href='https://console.cloud.google.com/functions/details/us-central1/calculatorSendMonthlyStatements?tab=logs'
          isExternal
          color='teal.600'
        >
          Open calculatorSendMonthlyStatements GCF logs
        </Link>
        <InfoHint label='Use these logs to confirm statement generation counts match expectations before any live send.' />
      </HStack>
      <HStack spacing={1} alignItems='center'>
        <Checkbox isChecked={skipEmail} onChange={(event) => setSkipEmail(event.target.checked)}>
          Skip Email
        </Checkbox>
        <InfoHint label='Skip Email allows a dry execution path where send logic runs but customer emails are not delivered.' />
      </HStack>
      <HStack spacing={1} alignSelf='flex-start'>
        <Text fontSize='sm'>Debug Rental ID (optional)</Text>
        <InfoHint label='Optional filter to target a single rental for debugging instead of processing the full cohort.' />
        <IconButton
          aria-label={showDebugRentalId ? 'Hide debug rental id' : 'Show debug rental id'}
          size='sm'
          variant='unstyled'
          minW='20px'
          h='20px'
          icon={
            showDebugRentalId ? (
              <ChevronDownIcon className='h-5 text-slate-600' />
            ) : (
              <ChevronRightIcon className='h-5 text-slate-600' />
            )
          }
          onClick={toggleDebugRentalId}
        />
      </HStack>
      <Collapse in={showDebugRentalId} animateOpacity>
        <FormControl maxW='360px'>
          <Input value={debugRentalId} onChange={(event) => setDebugRentalId(event.target.value)} />
        </FormControl>
      </Collapse>
      <HStack spacing={1} alignSelf='flex-start' alignItems='center'>
        <Button
          colorScheme='teal'
          variant='solid'
          onClick={onSend}
          isDisabled={isLoading}
          alignSelf='flex-start'
        >
          Trigger Test Send
        </Button>
        <InfoHint label='Publishes to calculator-send-monthly-statements without isLiveSend=true, used to validate behavior before live execution.' />
      </HStack>
      {error && (
        <Alert status='error'>
          <AlertIcon />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {success && (
        <Alert status='success'>
          <AlertIcon />
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}
    </Stack>
  )
}

export default TestSend
